import { ApiError } from 'src/api/client';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { Button } from 'src/components/ui/Button';
import { Notice } from 'src/components/ui/Notice';

type LoadFailureProps = {
  /** What could not be loaded, in the editor's terms — not "Error 500". */
  title: string;
  error: unknown;
  /** Omitted when the screen has no single query to retry. */
  onRetry?: () => void;
  /**
   * Shown when the endpoint itself answered 404 — as opposed to the record being missing.
   * The dashboard and the API deploy separately, so a screen can reach production ahead of the
   * routes it needs, and "HTTP 404" alone sends an editor hunting for a bug that is a deploy.
   */
  whenMissing?: string;
};

/**
 * A request that failed is not an empty stage.
 *
 * Art Direction §13: every workspace already renders "the pipeline produced nothing here" as a
 * deliberate absence. A dropped connection rendered the same way tells an editor that a broadcast
 * has no stories, which is the one reading that must never happen — they would go looking for the
 * bug in the pipeline instead of reloading the page.
 */
export function LoadFailure({ title, error, onRetry, whenMissing }: LoadFailureProps) {
  // A 404 from a stage endpoint is not a failure — it is the stage saying it has not run. Rendered
  // red under "no se pudieron cargar los temas", it reads as a malfunction and sends an editor
  // hunting for a bug that does not exist; Art Direction §7 asks for the four absences to be told
  // apart, and this is the one that was wearing the wrong one. Handled here rather than in each
  // workspace because every one of them made the same mistake in the same way.
  if (error instanceof ApiError && error.status === 404) {
    return (
      <AbsenceState
        kind="not_reached"
        what={whenMissing || 'Esta etapa todavía no ha corrido para esta transmisión.'}
        why={error.message}
        wouldChange="Aparecerá en cuanto la máquina procese esta etapa. Arriba dice si ya está en cola."
      />
    );
  }

  const message = error instanceof Error ? error.message : 'Error desconocido';
  const origin = error instanceof ApiError ? `${error.status === 0 ? 'sin respuesta' : error.status} · ${error.path}` : null;
  const missing = whenMissing && error instanceof ApiError && error.status === 404 ? whenMissing : null;

  return (
    <Notice tone="contradicted" title={title} role="alert">
      <p>{message}</p>
      {missing && <p>{missing}</p>}
      {/* The endpoint, so a report says which one — the editor is the one who sees this first. */}
      {origin && <p className="mono">{origin}</p>}
      {onRetry && (
        <p className="notice__action">
          <Button onClick={onRetry}>Reintentar</Button>
        </p>
      )}
    </Notice>
  );
}
